import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { bs } from '../../../styles/styles';
import { InputFieldError } from '../../../components/InputFieldError';
import { AutomationStepCardTextingInput } from '../../../components/automation/AutomationStepCard/presentation';
import { styleSheet as cardStyleSheet } from '../../../components/automation/AutomationStepCard/styles';

interface TextingStepFormProps {
	step: Api.ITextingAutomationStep | null;
	trigger: Api.IAutomationTrigger | null;
	schedule: Api.IAutomationStepSchedule | null;
	readonly: boolean;
	showRequiredFieldError: boolean;
	onSaveStep: (
		stepToSave?: Partial<Api.ITextingAutomationStep>,
		newSchedule?: Api.IAutomationStepSchedule
	) => Promise<void>;
	onClearError: () => void;
}

/**
 * Form component for editing texting automation step content.
 * Displays a text editor for composing SMS/text messages with merge fields.
 */
export function TextingStepForm({
	step,
	trigger,
	schedule,
	readonly,
	showRequiredFieldError,
	onSaveStep,
	onClearError,
}: TextingStepFormProps) {
	const hasTextContent = !!step?.content;

	const errorMessage = React.useMemo(() => {
		if (showRequiredFieldError && !hasTextContent) {
			return 'Please provide text content.';
		}
		return null;
	}, [showRequiredFieldError, hasTextContent]);

	if (!step || !trigger || !schedule) {
		return <div>Loading step data...</div>;
	}

	// The AutomationStepCardTextingInput expects a TextingAutomationStepViewModel
	// which is a MobX observable wrapper, but in the new architecture we use
	// plain ITextingAutomationStep. The component internally calls step.toJs()
	// which doesn't exist on the plain interface, but the component gracefully
	// handles this with || {} fallback.
	// Type assertion is necessary here until AutomationStepCardTextingInput
	// is refactored to accept plain step objects instead of view models.
	const stepAsViewModel = step as unknown as Api.TextingAutomationStepViewModel;

	return (
		<>
			<div className={css(cardStyleSheet.fieldLabel)}>
				Text content: &nbsp;
				<span className={css(bs.required)}>*</span>
			</div>
			<InputFieldError errorMessage={errorMessage}>
				<AutomationStepCardTextingInput
					index={0}
					onRequestClearErrorMessages={onClearError}
					onRequestSave={onSaveStep}
					onStepChanged={() => {
						// No-op: step changes are handled via onSaveStep callback
					}}
					schedule={schedule}
					step={stepAsViewModel}
					trigger={trigger}
					readOnly={readonly}
				/>
			</InputFieldError>
		</>
	);
}
